import { useState, useCallback, useEffect, useId } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AlertCircle, CheckCircle2, Info } from 'lucide-react'
import { getClinicalSelectionStatus } from './clinicalSelectionValidation'
import { ImplantCatalogCombobox } from './ImplantCatalogCombobox'

export type ImplantTypesMap = Record<string, string>

interface OdontogramProps {
    companyId?: Id<'companies'>
    selectedTeeth: string[]
    selectedImplants: string[]
    /** ID catalogo impianto per posizione. */
    implantTypes?: ImplantTypesMap
    onSelectionChange: (
        teeth: string[],
        implants: string[],
        implantTypes: ImplantTypesMap
    ) => void
    disabled?: boolean
    pricingMode?: 'per_tooth' | 'per_arch' | 'per_mouth'
    showValidationErrors?: boolean
    statusId?: string
    className?: string
}

const TOOTH_LAYOUT = {
    upper: [[18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28]],
    lower: [[48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38]],
}

type ToothPosition = {
    number: number
    x: number
    y: number
    labelX: number
    labelY: number
    rotation: number
    scale: number
}

const TOOTH_SHAPE = 'M -10 -14 C -14 -9 -13 3 -9 13 C -6 18 6 18 9 13 C 13 3 14 -9 10 -14 C 5 -18 -5 -18 -10 -14 Z'

function createArchPositions(
    numbers: number[],
    startAngle: number,
    endAngle: number
): ToothPosition[] {
    const centerX = 260
    const centerY = 160
    const radiusX = 132
    const radiusY = 90
    const labelRadiusX = 164
    const labelRadiusY = 115

    return numbers.map((number, index) => {
        const progress = index / (numbers.length - 1)
        const angle = startAngle + (endAngle - startAngle) * progress
        const radians = (angle * Math.PI) / 180
        const distanceFromCenter = Math.abs(index - (numbers.length - 1) / 2)
        const scale = distanceFromCenter > 5.5 ? 1.16 : distanceFromCenter > 3.5 ? 1.04 : 0.9

        return {
            number,
            x: centerX + radiusX * Math.cos(radians),
            y: centerY + radiusY * Math.sin(radians),
            labelX: centerX + labelRadiusX * Math.cos(radians),
            labelY: centerY + labelRadiusY * Math.sin(radians),
            rotation: angle + 90,
            scale,
        }
    })
}

const UPPER_ARCH_POSITIONS = createArchPositions(TOOTH_LAYOUT.upper[0], 195, 345)
const LOWER_ARCH_POSITIONS = createArchPositions(TOOTH_LAYOUT.lower[0], 165, 15)

function pruneImplantTypes(
    implants: string[],
    types: ImplantTypesMap
): ImplantTypesMap {
    const next: ImplantTypesMap = {}
    for (const pos of implants) {
        if (types[pos]?.trim()) next[pos] = types[pos]
    }
    return next
}

function isValidArchOnlySelection(teeth: string[]): boolean {
    if (teeth.length === 0) return true
    const selected = new Set(teeth)
    const upper = TOOTH_LAYOUT.upper.flat().map(String)
    const lower = TOOTH_LAYOUT.lower.flat().map(String)
    const hasUpper = upper.every((tooth) => selected.has(tooth))
    const hasLower = lower.every((tooth) => selected.has(tooth))
    const expectedSize = (hasUpper ? upper.length : 0) + (hasLower ? lower.length : 0)
    return expectedSize > 0 && selected.size === expectedSize
}

export function Odontogram({
    companyId,
    selectedTeeth,
    selectedImplants,
    implantTypes = {},
    onSelectionChange,
    disabled = false,
    pricingMode,
    showValidationErrors = false,
    statusId,
    className,
}: OdontogramProps) {
    const [mode, setMode] = useState<'teeth' | 'implants'>('teeth')
    const [showAllImplants, setShowAllImplants] = useState(false)
    const generatedId = useId()
    const archOnly = pricingMode === 'per_arch'
    const implantCatalogResult = useQuery(api.implantTypes.listForPrescription, {
        company_id: companyId,
        include_all: showAllImplants,
    })
    const implantCatalog = implantCatalogResult?.items
    const canShowAllImplants = Boolean(
        implantCatalogResult?.configured &&
        !implantCatalogResult.showing_all &&
        implantCatalogResult.total_count > implantCatalogResult.scoped_count
    )

    useEffect(() => {
        setShowAllImplants(false)
    }, [companyId])

    // Una prescrizione esistente può contenere un impianto attivo fuori dalla
    // selezione iniziale della società (per esempio scelto con “Mostra tutti”).
    // In quel caso espandiamo il catalogo per conservarne nome e marca; soltanto
    // un ID assente anche dal catalogo globale resta presentato come storico.
    useEffect(() => {
        if (
            !implantCatalogResult?.configured ||
            implantCatalogResult.showing_all ||
            implantCatalogResult.total_count === implantCatalogResult.scoped_count
        ) {
            return
        }
        const visibleIds = new Set(implantCatalogResult.items.map((item) => String(item._id)))
        const hasSelectionOutsideCompanyCatalog = Object.values(implantTypes).some(
            (implantTypeId) => implantTypeId.trim() !== '' && !visibleIds.has(implantTypeId)
        )
        if (hasSelectionOutsideCompanyCatalog) setShowAllImplants(true)
    }, [implantCatalogResult, implantTypes])

    useEffect(() => {
        if (!archOnly) return
        if (!isValidArchOnlySelection(selectedTeeth)) {
            onSelectionChange([], selectedImplants, implantTypes)
        }
    }, [archOnly, implantTypes, onSelectionChange, selectedImplants, selectedTeeth])

    // Se si rimuove un impianto dall'esterno, i tipi restano allineati
    useEffect(() => {
        const pruned = pruneImplantTypes(selectedImplants, implantTypes)
        if (Object.keys(pruned).length !== Object.keys(implantTypes).length) {
            onSelectionChange(selectedTeeth, selectedImplants, pruned)
        }
        // solo allineamento chiavi orfane
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedImplants.join(',')])

    const emit = useCallback(
        (teeth: string[], implants: string[], types: ImplantTypesMap) => {
            onSelectionChange(teeth, implants, pruneImplantTypes(implants, types))
        },
        [onSelectionChange]
    )

    const handleToothClick = useCallback(
        (toothNumber: string) => {
            if (disabled) return

            if (mode === 'teeth') {
                if (archOnly) return
                const newSelectedTeeth = selectedTeeth.includes(toothNumber)
                    ? selectedTeeth.filter((t) => t !== toothNumber)
                    : [...selectedTeeth, toothNumber]
                // Impianti indipendenti: non li tocchiamo quando si deseleziona un dente
                emit(newSelectedTeeth, selectedImplants, implantTypes)
            } else {
                const newSelectedImplants = selectedImplants.includes(toothNumber)
                    ? selectedImplants.filter((i) => i !== toothNumber)
                    : [...selectedImplants, toothNumber]
                emit(selectedTeeth, newSelectedImplants, implantTypes)
            }
        },
        [mode, selectedTeeth, selectedImplants, implantTypes, emit, disabled, archOnly]
    )

    const selectAll = useCallback(() => {
        if (disabled) return
        const allTeeth = [
            ...TOOTH_LAYOUT.upper.flat(),
            ...TOOTH_LAYOUT.lower.flat(),
        ].map(String)

        if (archOnly && mode === 'teeth') {
            emit(allTeeth, selectedImplants, implantTypes)
        } else if (mode === 'teeth') {
            emit(allTeeth, selectedImplants, implantTypes)
        } else {
            emit(selectedTeeth, allTeeth, implantTypes)
        }
    }, [mode, selectedTeeth, selectedImplants, implantTypes, emit, disabled, archOnly])

    const selectUpperArch = useCallback(() => {
        if (disabled) return
        const upperTeeth = TOOTH_LAYOUT.upper.flat().map(String)

        if (archOnly && mode === 'teeth') {
            emit([...new Set([...selectedTeeth, ...upperTeeth])], selectedImplants, implantTypes)
        } else if (mode === 'teeth') {
            emit(
                [...new Set([...selectedTeeth, ...upperTeeth])],
                selectedImplants,
                implantTypes
            )
        } else {
            emit(
                selectedTeeth,
                [...new Set([...selectedImplants, ...upperTeeth])],
                implantTypes
            )
        }
    }, [mode, selectedTeeth, selectedImplants, implantTypes, emit, disabled, archOnly])

    const selectLowerArch = useCallback(() => {
        if (disabled) return
        const lowerTeeth = TOOTH_LAYOUT.lower.flat().map(String)

        if (archOnly && mode === 'teeth') {
            emit([...new Set([...selectedTeeth, ...lowerTeeth])], selectedImplants, implantTypes)
        } else if (mode === 'teeth') {
            emit(
                [...new Set([...selectedTeeth, ...lowerTeeth])],
                selectedImplants,
                implantTypes
            )
        } else {
            emit(
                selectedTeeth,
                [...new Set([...selectedImplants, ...lowerTeeth])],
                implantTypes
            )
        }
    }, [mode, selectedTeeth, selectedImplants, implantTypes, emit, disabled, archOnly])

    const clearAll = useCallback(() => {
        if (disabled) return
        if (archOnly && mode === 'teeth') {
            emit([], selectedImplants, implantTypes)
        } else if (mode === 'teeth') {
            emit([], selectedImplants, implantTypes)
        } else {
            emit(selectedTeeth, [], {})
        }
    }, [mode, selectedTeeth, selectedImplants, implantTypes, emit, disabled, archOnly])

    const setImplantType = (pos: string, value: string) => {
        emit(selectedTeeth, selectedImplants, {
            ...implantTypes,
            [pos]: value,
        })
    }

    const renderTooth = (position: ToothPosition) => {
        const toothStr = String(position.number)
        const isToothSelected = selectedTeeth.includes(toothStr)
        const isImplantSelected = selectedImplants.includes(toothStr)
        const isCurrentModeSelected =
            mode === 'teeth' ? isToothSelected : isImplantSelected
        const isIndividualSelectionDisabled = disabled || (archOnly && mode === 'teeth')
        const selectionColor = mode === 'teeth' ? '#7A64B4' : '#FF6B35'
        const supportingFill = isToothSelected
            ? '#F3F0FA'
            : isImplantSelected
                ? '#FFF1EB'
                : '#FFFFFF'

        return (
            <g
                id={`${generatedId}-tooth-${position.number}`}
                key={position.number}
                role="button"
                tabIndex={isIndividualSelectionDisabled ? -1 : 0}
                aria-disabled={isIndividualSelectionDisabled}
                aria-pressed={isCurrentModeSelected}
                aria-label={`Dente ${position.number}${isToothSelected ? ', dente selezionato' : ''}${isImplantSelected ? ', impianto selezionato' : ''}`}
                onClick={() => handleToothClick(toothStr)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleToothClick(toothStr)
                    }
                }}
                className={cn(
                    'group outline-hidden',
                    isIndividualSelectionDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                )}
            >
                <g transform={`translate(${position.x} ${position.y}) rotate(${position.rotation}) scale(${position.scale})`}>
                    <circle
                        cx="0"
                        cy="0"
                        r="20"
                        fill="transparent"
                        stroke="transparent"
                        strokeWidth="3"
                        pointerEvents="all"
                        vectorEffect="non-scaling-stroke"
                        className="transition-colors group-focus-visible:stroke-primary"
                    />
                    {isCurrentModeSelected && (
                        <path
                            d={TOOTH_SHAPE}
                            fill="none"
                            stroke={selectionColor}
                            strokeWidth="7"
                            opacity="0.2"
                        />
                    )}
                    <path
                        d={TOOTH_SHAPE}
                        fill={isCurrentModeSelected ? selectionColor : supportingFill}
                        stroke={isCurrentModeSelected ? selectionColor : '#CBD5E1'}
                        strokeWidth="1.5"
                        vectorEffect="non-scaling-stroke"
                        className="transition-[fill,stroke] duration-200 group-hover:stroke-secondary group-focus-visible:stroke-primary"
                    />
                    <path
                        d="M -5 -1 L -1 4 L 7 -7"
                        fill="none"
                        stroke={isCurrentModeSelected ? '#FFFFFF' : '#CBD5E1'}
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity={isCurrentModeSelected ? 1 : 0.35}
                        vectorEffect="non-scaling-stroke"
                    />
                    {((isToothSelected && mode === 'implants') || (isImplantSelected && mode === 'teeth')) && (
                        <circle
                            cx="0"
                            cy="-21"
                            r="3.5"
                            fill={isToothSelected ? '#7A64B4' : '#FF6B35'}
                            stroke="#FFFFFF"
                            strokeWidth="1.5"
                            vectorEffect="non-scaling-stroke"
                        />
                    )}
                </g>
                <text
                    x={position.labelX}
                    y={position.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className={cn(
                        'select-none fill-gray-500 text-[14px] font-semibold tabular-nums',
                        isToothSelected && 'fill-secondary font-semibold',
                        isImplantSelected && mode === 'implants' && 'fill-primary font-semibold'
                    )}
                    aria-hidden="true"
                >
                    {position.number}
                </text>
                {isCurrentModeSelected && (
                    <circle
                        cx={position.labelX + 12}
                        cy={position.labelY - 8}
                        r="2.5"
                        fill={selectionColor}
                        aria-hidden="true"
                    />
                )}
            </g>
        )
    }

    const selectionStatus = getClinicalSelectionStatus({
        selectedTeeth,
        selectedImplants,
        implantTypes,
        pricingMode,
    })
    const resolvedStatusId = statusId ?? `${generatedId}-clinical-selection-status`
    const showAsError = showValidationErrors && !selectionStatus.valid
    const sortedImplants = [...selectedImplants].sort(
        (a, b) => parseInt(a, 10) - parseInt(b, 10)
    )

    const handleStatusAction = () => {
        if (selectionStatus.actionMode === 'implants') {
            setMode('implants')
            const firstMissingPosition = selectionStatus.missingImplantPositions[0]
            if (firstMissingPosition) {
                requestAnimationFrame(() => {
                    document.getElementById(`${generatedId}-implant-${firstMissingPosition}`)?.focus()
                })
            }
            return
        }

        setMode('teeth')
        requestAnimationFrame(() => {
            const targetId = archOnly
                ? `${generatedId}-upper-arch-action`
                : `${generatedId}-tooth-11`
            document.getElementById(targetId)?.focus()
        })
    }

    return (
        <div className={cn('space-y-3', className)}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-600">
                    {archOnly && mode === 'teeth'
                        ? 'Scegli una o entrambe le arcate.'
                        : mode === 'teeth'
                            ? 'Seleziona i denti della lavorazione.'
                            : 'Seleziona le posizioni, poi indica il tipo di impianto.'}
                </p>
                <div className="inline-flex w-full rounded-md border border-gray-200 bg-gray-50 p-1 sm:w-auto" role="group" aria-label="Tipo di selezione">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setMode('teeth')}
                        disabled={disabled}
                        aria-pressed={mode === 'teeth'}
                        aria-label={`Denti obbligatori, ${selectedTeeth.length} selezionati`}
                        className={cn(
                            'flex-1 border border-transparent shadow-none sm:flex-none',
                            mode === 'teeth' && 'border-secondary/20 bg-white text-secondary shadow-sm hover:bg-white hover:text-secondary'
                        )}
                    >
                        Denti · {selectedTeeth.length}
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setMode('implants')}
                        disabled={disabled}
                        aria-pressed={mode === 'implants'}
                        aria-label={`Impianti facoltativi, ${selectedImplants.length} selezionati`}
                        className={cn(
                            'flex-1 border border-transparent shadow-none sm:flex-none',
                            mode === 'implants' && 'border-primary/20 bg-white text-primary shadow-sm hover:bg-white hover:text-primary'
                        )}
                    >
                        Impianti · {selectedImplants.length}
                    </Button>
                </div>
            </div>

            {!disabled && <div
                id={resolvedStatusId}
                tabIndex={-1}
                role={showAsError ? 'alert' : 'status'}
                aria-live={showAsError ? 'assertive' : 'polite'}
                aria-atomic="true"
                className={cn(
                    'flex flex-wrap items-start gap-2 rounded-md border px-3 py-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:flex-nowrap',
                    selectionStatus.valid
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                        : showAsError
                            ? 'border-red-200 bg-red-50 text-red-950'
                            : 'border-blue-200 bg-blue-50 text-blue-950'
                )}
            >
                {selectionStatus.valid ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                ) : showAsError ? (
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                ) : (
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <p className="min-w-[12rem] flex-1 text-sm leading-5">
                    <span className="font-semibold">{selectionStatus.title}.</span>{' '}
                    {selectionStatus.description}
                </p>
                {!selectionStatus.valid && selectionStatus.actionMode && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleStatusAction}
                        disabled={disabled}
                        className="ml-6 h-9 shrink-0 border-current/25 bg-white/70 px-2.5 text-xs sm:ml-0"
                    >
                        {selectionStatus.actionMode === 'teeth' ? 'Seleziona i denti' : 'Completa impianti'}
                    </Button>
                )}
            </div>}

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-wrap items-center justify-center gap-1.5 border-b border-gray-100 bg-gray-50/70 px-2 py-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={selectAll}
                        disabled={disabled}
                        aria-label={archOnly && mode === 'teeth' ? 'Seleziona entrambe le arcate' : `Seleziona tutti ${mode === 'teeth' ? 'i denti' : 'gli impianti'}`}
                        className="h-9 px-2.5"
                    >
                        {archOnly && mode === 'teeth' ? 'Entrambe' : 'Tutto'}
                    </Button>
                    <Button
                        id={`${generatedId}-upper-arch-action`}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={selectUpperArch}
                        disabled={disabled}
                        aria-label={`Seleziona ${mode === 'teeth' ? 'i denti dell’arcata' : 'gli impianti dell’arcata'} superiore`}
                        className="h-9 px-2.5"
                    >
                        Superiore
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={selectLowerArch}
                        disabled={disabled}
                        aria-label={`Seleziona ${mode === 'teeth' ? 'i denti dell’arcata' : 'gli impianti dell’arcata'} inferiore`}
                        className="h-9 px-2.5"
                    >
                        Inferiore
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearAll}
                        disabled={disabled}
                        aria-label={mode === 'teeth' ? 'Deseleziona tutti i denti' : 'Rimuovi tutti gli impianti'}
                        className="h-9 px-2.5"
                    >
                        Azzera
                    </Button>
                </div>

                <div className="px-1 py-1 sm:px-3 sm:py-2">
                    <svg
                        viewBox="0 0 520 320"
                        className="mx-auto block h-auto w-full max-w-[520px]"
                        role="group"
                        aria-label={`Odontogramma FDI, modalità ${mode === 'teeth' ? 'denti' : 'impianti'}`}
                    >
                        <path
                            d="M 132 137 C 148 94 196 70 260 70 C 324 70 372 94 388 137"
                            fill="none"
                            stroke="#E2E8F0"
                            strokeWidth="1.5"
                            strokeDasharray="3 6"
                            aria-hidden="true"
                        />
                        <path
                            d="M 132 183 C 148 226 196 250 260 250 C 324 250 372 226 388 183"
                            fill="none"
                            stroke="#E2E8F0"
                            strokeWidth="1.5"
                            strokeDasharray="3 6"
                            aria-hidden="true"
                        />
                        <line x1="260" y1="28" x2="260" y2="292" stroke="#F1F5F9" strokeWidth="1" aria-hidden="true" />
                        <text x="260" y="16" textAnchor="middle" className="fill-gray-400 text-[11px] font-medium uppercase tracking-[0.12em]" aria-hidden="true">
                            Arcata superiore
                        </text>
                        <text x="260" y="308" textAnchor="middle" className="fill-gray-400 text-[11px] font-medium uppercase tracking-[0.12em]" aria-hidden="true">
                            Arcata inferiore
                        </text>
                        {UPPER_ARCH_POSITIONS.map(renderTooth)}
                        {LOWER_ARCH_POSITIONS.map(renderTooth)}
                    </svg>
                </div>
            </div>

            {sortedImplants.length > 0 && (
                <div className="space-y-2.5 rounded-lg border bg-orange-50/40 p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-gray-600">
                            <span className="font-semibold text-gray-900">Configura gli impianti.</span>{' '}
                            Scegli nome e marca per ogni posizione.
                        </p>
                        {canShowAllImplants && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowAllImplants(true)}
                                disabled={disabled}
                                className="w-full shrink-0 bg-white sm:w-auto"
                            >
                                Mostra tutti ({implantCatalogResult?.total_count})
                            </Button>
                        )}
                    </div>
                    {canShowAllImplants && (
                        <p className="text-xs text-gray-500">
                            Catalogo della società: {implantCatalogResult?.scoped_count} impianti configurati.
                        </p>
                    )}
                    {implantCatalog?.length === 0 && (
                        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                            <p>
                                {canShowAllImplants
                                    ? 'Nessun impianto configurato è disponibile. Usa “Mostra tutti” per consultare il catalogo completo.'
                                    : 'Il catalogo impianti non contiene opzioni disponibili. Rimuovi le posizioni implantari oppure contatta PrimoLab.'}
                            </p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {sortedImplants.map((pos) => {
                            const selectId = `${generatedId}-implant-${pos}`
                            const errorId = `${selectId}-error`
                            const hasError = !implantTypes[pos]?.trim()
                            return (
                            <div key={pos} className="min-w-0">
                                <label htmlFor={selectId} className="mb-1 block text-sm font-medium text-gray-800">
                                    Posizione {pos} — nome e marca *
                                </label>
                                <ImplantCatalogCombobox
                                    id={selectId}
                                    value={implantTypes[pos] ?? ''}
                                    items={implantCatalog}
                                    onValueChange={(value) => setImplantType(pos, value)}
                                    disabled={disabled}
                                    loading={implantCatalog === undefined}
                                    invalid={showValidationErrors && hasError}
                                    describedBy={showValidationErrors && hasError ? errorId : undefined}
                                    searchLabel={`Cerca impianto per la posizione ${pos}`}
                                />
                                {showValidationErrors && hasError && (
                                    <p id={errorId} className="mt-1 text-sm text-red-700" role="alert">
                                        Scegli nome e marca per l&apos;impianto in posizione {pos}.
                                    </p>
                                )}
                            </div>
                        )})}
                    </div>
                </div>
            )}
        </div>
    )
}
